import React, { Component, Fragment } from 'react';
import { reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Form } from 'reactstrap';
import { Card, CardBody } from 'reactstrap';
import BasicsFieldset from './fieldsets/BasicsFieldset';
import LocationFieldset from './fieldsets/LocationFieldset';
import ShortDescFieldset from './fieldsets/ShortDescFieldset';
import TicketingQuestionsFieldset from './fieldsets/TicketingQuestionsFieldset';
import ShortTicketFieldset from './fieldsets/ShortTicketFieldset';
import TagsFieldset from './fieldsets/TagsFieldset';
import SingleLinkFieldset from './fieldsets/SingleLinkFieldset';
import DateTimeFieldset from './fieldsets/DateTimeFieldset';
import ImagesFieldset from './fieldsets/ImagesFieldset';
import OrganiserFieldset from './fieldsets/OrganiserFieldset';
import OpenHouseFieldset from './fieldsets/OpenHouseFieldset';
import InfoLink from 'components/InfoLink';
import { stepValidationFactory } from 'actions/submissionFormActions';
import scrollToFirstError from 'lib/utils/scrollToFirstError';
import belongsTo from 'lib/utils/belongsTo';
import styles from './QuickForm.scss';
import TitleHeader from '../TitleHeader';

const CardSection = (props) => {
  const rowClasses = `row justify-content-center ${props.rowClass}`;

  return (
    <div className={rowClasses}>
      <div className="col-md-10">
        <Card>
          {props.children}
        </Card>
      </div>
    </div>
  );
};

class QuickForm extends Component {
  static propTypes = {
    categories: PropTypes.array.isRequired,
    timeZones: PropTypes.array.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    formErrors: PropTypes.object,
    isLoading: PropTypes.bool,
    config: PropTypes.object.isRequired,
    change: PropTypes.func.isRequired,
    availableCountries: PropTypes.object.isRequired,
    onClearAddress: PropTypes.func.isRequired,
    onVenueSelect: PropTypes.func.isRequired,
    onChangeCountry: PropTypes.func.isRequired,
    onChangeZip: PropTypes.func.isRequired,
    currentUser: ImmutablePropTypes.map,
    pricingTiers: ImmutablePropTypes.map.isRequired,
    currencies: PropTypes.array.isRequired,
    sub_categories: PropTypes.array,
    category_id: PropTypes.number,
    urlTypes: PropTypes.array.isRequired,
    imageErrorVisible: PropTypes.bool.isRequired,
    ssoGeotixUrl: PropTypes.string,
    ticketingUrlType: PropTypes.string,
    premiumEdit: PropTypes.bool,
    online: PropTypes.bool,
    imagesRequired: PropTypes.bool
  }

  clearAddress = () => {
    this.props.change('venue', {});
    this.props.onClearAddress();
  }

  render() {
    const {
      formErrors, isLoading, categories, config, pricingTiers, urlTypes,
      handleSubmit, availableCountries, onVenueSelect, currentUser, currencies,
      sub_categories, category_id, onChangeCountry, onChangeZip, online,
      imageErrorVisible, premiumEdit, ticketingUrlType, ssoGeotixUrl, timeZones,
      pricing_url,  imagesRequired, eventbriteEnabled } = this.props;

    const openHouseCategory = parseInt(config.get('open_house_category_id'));
    const selectedCategories = (sub_categories || []).map(c => c.id).concat(category_id);
    const showOpenHouse = openHouseCategory ?
      selectedCategories.some(cat => belongsTo(categories, openHouseCategory, cat)) : false;

    const geotixEdit = ticketingUrlType === 'geotix' && premiumEdit;
    const urlRequired = config.get('urls_required', true) && !pricing_url && !(ticketingUrlType === 'geotix');

    return (
      <Form onSubmit={handleSubmit}>
        <CardSection>
          <fieldset disabled={isLoading}>
            <TitleHeader title="Event Title" required/>
            <CardBody>
              <BasicsFieldset
                formErrors={formErrors}
                config={config}
                categories={categories}
                currentUser={currentUser}
                pricingTiers={pricingTiers}
              />
            </CardBody>
            <TitleHeader title="Event Location" infoUrl="https://support.evvnt.com/hc/en-us/articles/360020723054-Basics-Adding-your-event-venue" containerClass="mt-0" required/>
            <CardBody>
              <LocationFieldset
                formErrors={formErrors}
                onClearAddress={this.clearAddress}
                availableCountries={availableCountries}
                onVenueSelect={onVenueSelect}
                onChangeCountry={onChangeCountry}
                onChangeZip={onChangeZip}
                online={online}
              />
            </CardBody>
            <TitleHeader title="Event Time"/>
            <CardBody>
              { config.get('recurring_event') && <div className="pb-4">
                <span>To modify the dates in a recurring event, please visit the manage event page.</span>
              </div>
              }
              { !config.get('recurring_event') && <DateTimeFieldset formErrors={formErrors} categories={categories} timeZones={timeZones}/> }
            </CardBody>
            <TitleHeader title="Event Image" required={imagesRequired}>
              <InfoLink link="https://support.evvnt.com/hc/en-us/articles/360020923953"/>
            </TitleHeader>
            <CardBody>
              <ImagesFieldset formErrors={formErrors} imageErrorVisible={imageErrorVisible} />
            </CardBody>
            <ShortDescFieldset formErrors={formErrors} />
            {showOpenHouse && <OpenHouseFieldset formErrors={formErrors} currencies={currencies} />}
            <TagsFieldset/>
          </fieldset>
        </CardSection>
        <CardSection rowClass="mt-4">
          <fieldset disabled={isLoading}>
            <TitleHeader title="Tickets" infoUrl="https://support.evvnt.com/hc/en-us/articles/360020923153-Tickets-How-does-ticketing-work-for-your-event" required/>
            <CardBody className={styles.ticketsContainer}>
              {geotixEdit && <div className='tickets-layover'>
                <a href={ssoGeotixUrl} target='_blank' className='btn btn-success pull-right mt-3 mr-3'>
                  <i className="fa fa-ticket mr-2" />
                        Manage Tickets
                </a>
              </div>}
              <TicketingQuestionsFieldset formErrors={formErrors}/>
              <ShortTicketFieldset
                formErrors={formErrors}
                currencies={currencies}
                geotixEnabled={config.get('geotix_enabled')}
                premiumEdit={config.get('premium_edit')}
              />
            </CardBody>
            <TitleHeader title="Event Link" infoUrl="https://support.evvnt.com/hc/en-us/articles/360020725534-Links-Adding-links-to-your-event" containerClass="mt-0" required={urlRequired}/>
            <CardBody>
              <SingleLinkFieldset
                formErrors={formErrors}
                urlTypes={urlTypes}
                required={urlRequired}
              />
            </CardBody>
          </fieldset>
        </CardSection>
        <CardSection rowClass="mt-4">
          <fieldset disabled={isLoading}>
            <CardBody>
              <OrganiserFieldset formErrors={formErrors} isLoading={isLoading}/>
            </CardBody>
          </fieldset>
        </CardSection>
        <button type="submit" className="d-none"/>
      </Form>
    );
  }
}

export default reduxForm({
  form: 'eventSubmission',
  destroyOnUnmount: false,
  asyncValidate: stepValidationFactory('quick'),
  onSubmitFail: scrollToFirstError,
  persistentSubmitErrors: true,
})(QuickForm);



// WEBPACK FOOTER //
// app/client/bundles/EventSubmission/components/forms/QuickForm.jsx